/**
 * Parses a decimal coordinate stored as string form state.
 */
export const parsePropertyCoordinate = (
  value?: string | null
): number | null => {
  if (!value) {
    return null;
  }

  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

/**
 * Builds a readable property location query from the structured address fields.
 */
export const buildPropertyLocationQuery = (params: {
  addressDisplay?: string | null;
  streetName?: string | null;
  streetNumber?: string | null;
  unit?: string | null;
  neighborhood?: string | null;
  locality?: string | null;
  region?: string | null;
}): string | null => {
  const segments = [
    params.addressDisplay,
    [params.streetName, params.streetNumber].filter(Boolean).join(' ').trim(),
    params.unit,
    params.neighborhood,
    params.locality,
    params.region,
  ]
    .map((segment) => segment?.trim())
    .filter((segment): segment is string => Boolean(segment));

  if (segments.length === 0) {
    return null;
  }

  return Array.from(new Set(segments)).join(', ');
};

/**
 * Builds the external Google Maps URL for the current property location.
 */
export const buildPropertyGoogleMapsUrl = (params: {
  latitude?: string | null;
  longitude?: string | null;
  addressDisplay?: string | null;
  streetName?: string | null;
  streetNumber?: string | null;
  unit?: string | null;
  neighborhood?: string | null;
  locality?: string | null;
  region?: string | null;
}): string | null => {
  const latitude = parsePropertyCoordinate(params.latitude);
  const longitude = parsePropertyCoordinate(params.longitude);

  if (latitude != null && longitude != null) {
    return `https://www.google.com/maps?q=${latitude},${longitude}`;
  }

  const query = buildPropertyLocationQuery(params);
  if (!query) {
    return null;
  }

  return `https://www.google.com/maps?q=${encodeURIComponent(query)}`;
};

/**
 * Builds the embeddable map preview URL for the property editor.
 */
export const buildPropertyMapEmbedUrl = (params: {
  latitude?: string | null;
  longitude?: string | null;
  addressDisplay?: string | null;
  streetName?: string | null;
  streetNumber?: string | null;
  unit?: string | null;
  neighborhood?: string | null;
  locality?: string | null;
  region?: string | null;
}): string | null => {
  const baseUrl = buildPropertyGoogleMapsUrl(params);
  if (!baseUrl) {
    return null;
  }

  const url = new URL(baseUrl);
  url.searchParams.set('z', '16');
  url.searchParams.set('output', 'embed');
  return url.toString();
};
